import { useNavigate } from 'react-router-dom'
import { Location } from 'types'
import {
    getLocationId,
    getRefreshTokenUserId,
    getShopId,
    isNil,
    queryClient,
    refreshToken,
    updateLocationId,
    useMutationHandler,
    usePaginationHandler,
    useQueryHandler
} from 'utils'

const locationQuery = `

    id
    name
    phone
    email
    description
    image {
        id
        path
    }

    timezone
    
    businessAddress {
        country
        address
        suite
        city
        state
        zipCode
        latitude
        longitude
    }
    
    isDefault
    status
    
`

//useLocations:
export const useLocations = (search: string | null, limit: number, status?: boolean) => {
    const shopId = getShopId()
    return usePaginationHandler<Location[]>({
        query: `
            query getLocations(
                $shop: String!,
                $status: Boolean,
                $searchTerm: String,
                $limit: Int!,
                $after: String,
                $before: String
            ){
                getLocations(
                    shop: $shop,
                    status: $status,
                    searchTerm: $searchTerm,
                    limit: $limit,
                    after: $after,
                    before: $before
                ){
                    data{
                        ${locationQuery}
                    }
                    totalItems
                    remainingItems
                    nextCursor
                    previousCursor
                }
            }
        `,
        variables: {
            shop: shopId,
            status,
            searchTerm: search,
            limit,
            after: null,
            before: null
        },
        cacheKey: ['locations', shopId, search, status],
        limit,
        getResponse: json => json.data.getLocations,
        onSuccess: response => response.data
    })

}

//useLocation:
export const useLocation = (id: string, onNotFound?: (json: any) => void) => {
    const shopId = getShopId()
    return useQueryHandler<Location>({
        query: `
            query getLocation(
                $shop: String!,
                $locationId: String!
            ){
                getLocation(
                    shop: $shop,
                    locationId: $locationId
                ){
                    ${locationQuery}
                }
            }
        `,
        variables: {
            shop: shopId,
            locationId: id
        },
        cacheKey: ['location', id, shopId],
        onSuccess: json => json.data.getLocation,
        onNotFound
    })
}

export const useCurrentLocation = () => {

    const shopId = getShopId()
    const locationId = getLocationId()
    const hasLocationId = locationId !== ''
    const navigate = useNavigate()

    const removeLocation = () => {
        updateLocationId(null)
        queryClient.removeQueries(['currentLocation'])
        navigate(location.pathname)
    }
    
    //handler:
    return useQueryHandler<Location | null>({
        query: hasLocationId ? `
            query getLocation(
                $shop: String!,
                $locationId: String!
            ){
                getLocation(
                    shop: $shop,
                    locationId: $locationId
                ){
                    ${locationQuery}
                }
            }
        ` : `
            query getLocations(
                $shop: String!,
                $limit: Int!
            ){
                getLocations(
                    shop: $shop,
                    limit: $limit
                ){
                    data{
                        ${locationQuery}
                    }
                }
            }
        `,
        variables: hasLocationId ? {
            shop: shopId,
            locationId
        } : {
            shop: shopId,
            limit: 1
        },
        cacheKey: ['currentLocation', shopId, locationId, getRefreshTokenUserId(refreshToken())],
        enabled: !isNil(refreshToken()),
        onSuccess: json => {
            const _location: Location = hasLocationId ? json.data.getLocation : json.data.getLocations.data[0]
            if(!hasLocationId){
                updateLocationId(_location.id)
            }
            return _location
        },
        onNotFound: removeLocation,
        onForbidden: removeLocation,
        onUnknownError: removeLocation
    })

}

//create:
export const useCreateLocation = (location: Location) => {
    const shopId = getShopId()
    return useMutationHandler<null | boolean | 'name_is_used'>({
        query: `
            mutation createLocation(
                $shop: String!,
                $request: CreateLocationRequest!,
            ){
                createLocation(
                    shop: $shop,
                    request: $request
                ){
                    id
                }
            }
        `,
        variables: {
            shop: shopId,
            request: {

                name: location.name,
                phone: location.phone,
                email: location.email,
                description: location.description,
                image: location.image.id,

                timezone: location.timezone,

                businessAddress: location.businessAddress,

                isDefault: location.isDefault,
                status: location.status

            }

        },
        onSuccess: json => typeof json.data.createLocation.id === 'string',
        onError: json => {
            if(json.errors[0].extensions.errors[0].constraints[0].name === 'is_unique'){
                return 'name_is_used'
            }else{
                return null
            }
        }
    })

}

//update:
export const useUpdateLocation = (location: Location) => {
    const shopId = getShopId()
    return useMutationHandler<null | boolean | 'name_is_used'>({
        query: `
            mutation updateLocation(
                $shop: String!,
                $locationId: String!,
                $request: UpdateLocationRequest!,
            ){
                updateLocation(
                    shop: $shop,
                    locationId: $locationId,
                    request: $request
                ){
                    id
                }
            }
        `,
        variables: {
            shop: shopId,
            locationId: location.id,
            request: {

                name: location.name,
                phone: location.phone,
                email: location.email,
                description: location.description,
                image: location.image.id,

                timezone: location.timezone,

                businessAddress: location.businessAddress,

                isDefault: location.isDefault,
                status: location.status
                
            }

        },
        onSuccess: json => json.data.updateLocation.id === location.id,
        onError: json => {
            if(json.errors[0].extensions.errors[0].constraints[0].name === 'is_unique'){
                return 'name_is_used'
            }else{
                return null
            }
        }
    })
}
